import React, { FC, Fragment, useEffect, useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    Tooltip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ApolloError, NetworkStatus, useLazyQuery } from '@apollo/client';
import { reviewOrdersTemplate } from 'apollo/templates/order/query.graphql';
import { useDenthubErrorBoundary } from 'components/shared/denthub-error-boundary/denthub-error-boundary-context';
import InfiniteScroll from 'react-infinite-scroll-component';
import OrderReviewItem from '../order-review-item/order-review-item.component';
import { OrderSummary, ReviewOrders, OrderReviewFilterType, Invoice } from 'models/invoice';
import Loader from 'components/shared/loader/loader.component';
import AlertSnackbar from 'components/shared/alert-snackbar/alert-snackbar.component';
import { OrderBy, OrderDirection } from 'models/search-result';
import CreatePerOrderInvoiceDialog from '../create-per-order-invoice-dialog/create-per-order-invoice-dialog.component';
import { InvoicingState } from 'models/order';
import MissingCustomerNumberDialog from 'pages/manage-invoices/missing-customer-number-dialog/missing-customer-number-dialog.component';
import ErrorDialog from 'components/shared/error-dialog/error-dialog.component';
import useFixMissingScroll from 'utils/use-fix-missing-scroll';

type OrderReviewListProps = {
    setOrderSummary: (orderSummary: OrderSummary) => void;
    orderReviewFilter: OrderReviewFilterType;
};
const OrderReviewList: FC<OrderReviewListProps> = ({ orderReviewFilter, setOrderSummary }: OrderReviewListProps) => {
    const { t: translate } = useTranslation(['common', 'orders', 'invoices']);
    const [reviewOrders, setReviewOrders] = useState<ReviewOrders[]>([]);
    const [reviewOrder, setReviewOrder] = useState<ReviewOrders>({} as ReviewOrders);
    const [lastOffset, setLastOffset] = useState(0);
    const [clinicInvoiceEmail, setClinicInvoiceEmail] = useState('');
    const [openCreateInvoiceDialog, setOpenCreateInvoiceDialog] = useState(false);
    const [mutationError, setMutationError] = useState<Error | null>(null);
    const [orderBy, setOrderBy] = useState<OrderBy>({
        orderBy: 'deliveryDetails.date',
        orderDirection: OrderDirection.desc,
    });
    const [showErrorDialog, setShowErrorDialog] = useState(false);
    const { showBoundary } = useDenthubErrorBoundary();
    const [showMissingCustomerNumberDialog, setShowMissingCustomerNumberDialog] = useState(false);
    const getFormattedFilters = (invoiceFilter: OrderReviewFilterType) => {
        const filters = { ...invoiceFilter };
        delete filters.clinic;
        return filters;
    };

    const onToggleReadyForInvoice = (reviewOrder: ReviewOrders) => {
        const orders = [...reviewOrders];
        const index = orders.findIndex((order: ReviewOrders) => order._id === reviewOrder?._id);
        if (index > -1) {
            orders.splice(index, 1, reviewOrder);
        }
        // TODO: We have some data issues in invoice-backend / backend regarding OrderReview type
        // TODO: The mutation is doing everything correct, but the returned data is not right.
        // TODO: By not updating the frontend state this "works" right now. Temp hack fix.
        //setReviewOrders([...orders]);
    };

    const [getReviewOrders, { loading, fetchMore, networkStatus }] = useLazyQuery(reviewOrdersTemplate, {
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        notifyOnNetworkStatusChange: true,
        onCompleted: ({ reviewOrders }) => {
            setReviewOrders(reviewOrders?.orders);
            setOrderSummary(reviewOrders?.orderCount);
            setLastOffset(reviewOrders?.orders?.length);
        },
        onError(error) {
            showBoundary(error);
        },
    });

    useEffect(() => {
        getReviewOrders({
            variables: {
                filters: getFormattedFilters(orderReviewFilter),
                limit: 15,
                offset: 0,
                ...orderBy,
            },
        });
    }, [orderReviewFilter]);

    const onError = (error: ApolloError | null) => {
        if (error) {
            const [firstError] = error?.graphQLErrors;
            const errorType = (firstError.extensions?.exception as any)?.otherInfo?.errorType;
            if (errorType === 'INVOICE_NUM_REQUIRED') {
                setMutationError(new Error(translate(`invoices:${errorType}`)));
            } else {
                setMutationError(error);
            }
        }
    };

    const handleFetchMore = () => {
        if (fetchMore) {
            fetchMore({
                variables: {
                    offset: lastOffset,
                },
            });
        } else {
            getReviewOrders({
                variables: {
                    filters: getFormattedFilters(orderReviewFilter),
                    limit: 15,
                    offset: lastOffset,
                    ...orderBy,
                },
            });
        }
    };

    const setOrderOnField = async (field: string) => {
        const direction = orderBy.orderDirection === OrderDirection.asc ? OrderDirection.desc : OrderDirection.asc;
        getReviewOrders({
            variables: {
                filters: getFormattedFilters(orderReviewFilter),
                limit: 15,
                offset: 0,
                orderBy: field,
                orderDirection: direction,
            },
        });
        setOrderBy({
            orderBy: field,
            orderDirection: direction,
        });
    };

    const onCreateInvoice = (reviewOrder: ReviewOrders) => {
        setOpenCreateInvoiceDialog(true);
        setClinicInvoiceEmail(reviewOrder.clinicInvoiceEmail || '');
        setReviewOrder(reviewOrder);
    };

    const handleInvoiceCreation = (invoice: Invoice) => {
        const orders = [...reviewOrders];
        const index = orders.findIndex((rwOrder) => {
            return rwOrder._id === reviewOrder._id;
        });
        if (index > -1) {
            const rvOrder = {
                ...reviewOrder,
                invoiceNumber: invoice.invoiceNumber,
                invoiceStatus: invoice.status,
                invoiceType: invoice.invoiceType,
                invoicingState: InvoicingState.INVOICED,
            };
            orders.splice(index, 1, rvOrder);
            setReviewOrders(orders);
            setReviewOrder({} as ReviewOrders);
        }
        setOpenCreateInvoiceDialog(false);
    };

    const closeDialog = () => {
        setOpenCreateInvoiceDialog(false);
    };

    // Fix for infinite scroll not working on some screen sizes.
    useFixMissingScroll(handleFetchMore, reviewOrders?.length || 0);

    if ((loading && networkStatus !== NetworkStatus.fetchMore) || !reviewOrders) {
        return <Loader />;
    }
    return (
        <Fragment>
            {mutationError ? <AlertSnackbar type="error" message={mutationError?.message} /> : undefined}
            <TableContainer component={Paper}>
                <InfiniteScroll
                    dataLength={reviewOrders ? reviewOrders.length : 0}
                    next={handleFetchMore}
                    hasMore={true}
                    throttleTimeInMs={75}
                    loader={undefined}
                >
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell align="center">
                                    <Tooltip title={translate('orders:READY_FOR_INVOICE').toString()}>
                                        <span>{translate('invoices:READY')}</span>
                                    </Tooltip>
                                </TableCell>
                                <TableCell>{translate('common:CLINIC')}</TableCell>
                                <TableCell>{translate('orders:ORDER')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('deliveryDetails.date')}
                                        active={orderBy.orderBy === 'deliveryDetails.date'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:DELIVERY_DATE')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>{translate('invoices:INVOICE_TYPE')}</TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('invoiceNumber')}
                                        active={orderBy.orderBy === 'invoiceNumber'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:INVOICE')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('created.on')}
                                        active={orderBy.orderBy === 'created.on'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:ORDER_CREATED')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell>
                                    <TableSortLabel
                                        onClick={() => setOrderOnField('sent.on')}
                                        active={orderBy.orderBy === 'sent.on'}
                                        direction={orderBy.orderDirection}
                                    >
                                        {translate('orders:SENT')}
                                    </TableSortLabel>
                                </TableCell>
                                <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {reviewOrders.map((reviewOrder: ReviewOrders, index: number) => (
                                <OrderReviewItem
                                    handleToggleReadyForInvoice={onToggleReadyForInvoice}
                                    handleCreateInvoice={onCreateInvoice}
                                    openMissingCustomerNumberDialog={() => setShowMissingCustomerNumberDialog(true)}
                                    handleError={onError}
                                    key={index}
                                    reviewOrder={reviewOrder}
                                />
                            ))}
                        </TableBody>
                    </Table>
                </InfiniteScroll>
            </TableContainer>
            <CreatePerOrderInvoiceDialog
                open={openCreateInvoiceDialog}
                onCreateInvoice={handleInvoiceCreation}
                onDialogClose={closeDialog}
                orderId={reviewOrder._id}
                shouldSendEmail={reviewOrder.invoiceDeliveryMethod === 'EMAIL'}
                destInvoiceEmail={
                    reviewOrder.invoiceDeliveryMethod === 'EMAIL'
                        ? reviewOrder.clinicInvoiceEmail || ''
                        : clinicInvoiceEmail
                }
                invNumber={reviewOrder.tempInvoiceNumber || ''}
                clinicInvoiceDeliveryMethod={reviewOrder.invoiceDeliveryMethod || ''}
            />
            <MissingCustomerNumberDialog
                open={showMissingCustomerNumberDialog}
                onClose={() => {
                    setShowMissingCustomerNumberDialog(false);
                }}
            />
        </Fragment>
    );
};

export default OrderReviewList;
